import { useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";

interface DetailHeaderProps {
  drama: Drama;
  isFav: boolean;
  onToggleFav: () => void;
}

export default function DetailHeader({
  drama,
  isFav,
  onToggleFav,
}: DetailHeaderProps) {
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  const shareUrl = window.location.href;
  const shareTitle = `${drama.title} - 剧情介绍与演员信息`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast("链接已复制");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const shareWechat = async () => {
    await copyLink();
    setShareOpen(false);
    showToast("链接已复制，可粘贴到微信分享");
  };

  const shareWeibo = () => {
    const url = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(
      shareUrl
    )}&title=${encodeURIComponent(shareTitle)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setShareOpen(false);
  };

  return (
    <section className="relative w-full overflow-hidden">
      <img
        src={drama.banner}
        alt={`${drama.title}剧照`}
        title={`${drama.title} 剧集剧照`}
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/80 to-foreground-950/55" />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end">
          <span className="block h-56 w-40 shrink-0 overflow-hidden rounded-lg border border-background-50/20 bg-background-200 sm:h-64 sm:w-44">
            <img
              src={drama.poster}
              alt={`${drama.title}海报`}
              title={`${drama.title} 剧集海报`}
              className="h-full w-full object-cover object-top"
            />
          </span>

          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-3xl font-bold text-background-50 md:text-5xl">
              {drama.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-100/90">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-400" />
                <strong className="font-semibold text-background-50">
                  {drama.rating}
                </strong>
                / 10
              </span>
              <span>{drama.year}</span>
              <span>{drama.region}</span>
              <span>{drama.genre}</span>
              <span>全 {drama.episodes} 集</span>
              <span>{drama.duration}</span>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {drama.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-50/15 px-2.5 py-1 text-xs text-background-100"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="relative mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onToggleFav}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                  isFav
                    ? "bg-accent-500 text-foreground-950"
                    : "border border-background-50/50 bg-background-50/10 text-background-50 hover:bg-background-50/20"
                }`}
              >
                <i className={isFav ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {isFav ? "已收藏" : "收藏"}
              </button>

              <button
                type="button"
                onClick={() => setShareOpen((v) => !v)}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/50 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/20"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>

              <Link
                to={`/reviews/${drama.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-chat-quote-line" />
                写点评
              </Link>

              {shareOpen && (
                <div className="absolute left-0 top-full z-30 mt-2 w-48 rounded-lg border border-background-200 bg-background-50 p-2 shadow-lg">
                  <button
                    type="button"
                    onClick={shareWechat}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-wechat-line text-lg text-secondary-600" />
                    分享到微信
                  </button>
                  <button
                    type="button"
                    onClick={shareWeibo}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-weibo-line text-lg text-primary-600" />
                    分享到微博
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      copyLink();
                      setShareOpen(false);
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-link text-lg text-foreground-500" />
                    复制链接
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </section>
  );
}